import { useState, useEffect, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { normalizeImageUrl, PLACEHOLDER, getProductImagesArray } from '../../utils/productImage';

const MAX_THUMBS = 12;

const Gallery = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  max-width: 100%;
  min-width: 0;
`;

const MainImage = styled.div`
  position: relative;
  width: 100%;
  height: min(84vh, 680px);
  background: ${({ theme }) => theme.colors.secondary};
  overflow: hidden;

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    height: min(92vh, 800px);
  }
`;

const MainImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const OverlaySlot = styled.div`
  position: absolute;
  top: 16px;
  right: 16px;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: flex-end;
`;

const ThumbnailRow = styled.div`
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 0.5rem;
  max-width: 100%;
  min-width: 0;

  @media (max-width: 640px) {
    grid-template-columns: repeat(4, 1fr);
    gap: 0.375rem;
  }
`;

const ThumbnailButton = styled.button`
  aspect-ratio: 3 / 4;
  max-height: 4.5rem;
  border-radius: ${({ theme }) => theme.radius};
  overflow: hidden;
  border: 2px solid
    ${({ theme, $active }) => ($active ? theme.colors.foreground : 'transparent')};
  background: none;
  cursor: pointer;
  padding: 0;
  flex-shrink: 0;
  transition: border-color 0.2s ease;

  @media (max-width: 640px) {
    max-height: 3.5rem;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.foreground};
    outline-offset: 2px;
  }
`;

const ThumbnailImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const ViewAllRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
  max-width: 100%;
  min-width: 0;
`;

const ViewAllButton = styled.button`
  margin-top: 0.25rem;
  padding: 0.35rem 0.75rem;
  font-size: 0.875rem;
  font-family: ${({ theme }) => theme.fonts.sans};
  color: ${({ theme }) => theme.colors.foreground};
  background: transparent;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius};
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease;

  &:hover {
    background: ${({ theme }) => theme.colors.foreground};
    color: ${({ theme }) => theme.colors.cream};
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 200;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.88);
  padding: 1rem;
  animation: fadeInPDP 0.2s ease-out;
  @keyframes fadeInPDP {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const ModalContent = styled.div`
  position: relative;
  max-width: 90vw;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const ModalMainImage = styled.div`
  max-height: 75vh;
  max-width: 85vw;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => theme.colors.secondary};
  border-radius: calc(${({ theme }) => theme.radius} * 2);
  overflow: hidden;
`;

const ModalImg = styled.img`
  max-width: 100%;
  max-height: 75vh;
  object-fit: contain;
`;

const ModalCloseButton = styled.button`
  position: absolute;
  top: -2.5rem;
  right: 0;
  width: 2.5rem;
  height: 2.5rem;
  padding: 0;
  border: none;
  background: rgba(255, 255, 255, 0.2);
  color: #fff;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s;

  &:hover {
    background: rgba(255, 255, 255, 0.35);
  }
`;

const ModalNavButton = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 3rem;
  height: 3rem;
  padding: 0;
  border: none;
  background: rgba(255, 255, 255, 0.25);
  color: #fff;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s;
  z-index: 2;

  &:hover {
    background: rgba(255, 255, 255, 0.45);
  }
`;

const ModalCounter = styled.span`
  position: absolute;
  bottom: 4.5rem;
  left: 50%;
  transform: translateX(-50%);
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.875rem;
  font-family: ${({ theme }) => theme.fonts.sans};
`;

const ModalThumbnailStrip = styled.div`
  display: flex;
  gap: 0.5rem;
  justify-content: center;
  flex-wrap: wrap;
  max-height: 5rem;
  overflow-y: auto;
  padding: 0.25rem 0;
`;

const ModalThumbButton = styled.button`
  width: 3rem;
  height: 3rem;
  flex-shrink: 0;
  border-radius: ${({ theme }) => theme.radius};
  overflow: hidden;
  border: 2px solid
    ${({ theme, $active }) => ($active ? 'rgba(255,255,255,0.95)' : 'transparent')};
  background: none;
  cursor: pointer;
  padding: 0;

  &:hover {
    border-color: rgba(255, 255, 255, 0.7);
  }
`;

const ModalThumbImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

/**
 * Hero image · thumbnail strip · lightbox (maison/client ProductImageGallery parity).
 */
export default function ProductImageGallery({ product, productName, overlay }) {
  const displayImages = getProductImagesArray(product);
  const urls = displayImages.length > 0 ? displayImages : [PLACEHOLDER];

  const [selectedImage, setSelectedImage] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalIndex, setModalIndex] = useState(0);

  const productStableKey = useMemo(() => {
    if (!product) return '';
    if (product._id != null) return String(product._id);
    if (product.id != null) return String(product.id);
    if (product.slug != null) return String(product.slug);
    return '';
  }, [product]);

  const selected = Math.min(selectedImage, Math.max(0, urls.length - 1));
  const thumbs = urls.slice(0, MAX_THUMBS);
  const hasMore = urls.length > MAX_THUMBS;

  useEffect(() => {
    setSelectedImage(0);
  }, [productStableKey]);

  const closeModal = useCallback(() => setModalOpen(false), []);

  const goPrev = useCallback(() => {
    setModalIndex((i) => (i <= 0 ? urls.length - 1 : i - 1));
  }, [urls.length]);

  const goNext = useCallback(() => {
    setModalIndex((i) => (i >= urls.length - 1 ? 0 : i + 1));
  }, [urls.length]);

  const openModal = (start) => {
    setModalIndex(start);
    setModalOpen(true);
  };

  useEffect(() => {
    if (!modalOpen) return undefined;
    const handleKey = (e) => {
      if (e.key === 'Escape') closeModal();
      if (e.key === 'ArrowLeft') goPrev();
      if (e.key === 'ArrowRight') goNext();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [modalOpen, closeModal, goPrev, goNext]);

  return (
    <>
      <Gallery>
        <MainImage>
          <MainImg
            src={normalizeImageUrl(urls[selected])}
            alt={productName}
            width={800}
            height={1066}
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = PLACEHOLDER;
            }}
          />
          {overlay ? <OverlaySlot>{overlay}</OverlaySlot> : null}
        </MainImage>

        <ViewAllRow>
          <ThumbnailRow>
            {thumbs.map((img, index) => (
              <ThumbnailButton
                key={index}
                type="button"
                $active={selected === index}
                onClick={() => setSelectedImage(index)}
                aria-label={`View image ${index + 1}`}
                aria-current={selected === index ? 'true' : undefined}
              >
                <ThumbnailImg
                  src={normalizeImageUrl(img)}
                  alt=""
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = PLACEHOLDER;
                  }}
                />
              </ThumbnailButton>
            ))}
          </ThumbnailRow>
          {hasMore ? (
            <ViewAllButton type="button" onClick={() => openModal(selected)}>
              View all ({urls.length})
            </ViewAllButton>
          ) : null}
        </ViewAllRow>
      </Gallery>

      {modalOpen ? (
        <ModalOverlay onClick={closeModal} role="presentation">
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalCloseButton type="button" onClick={closeModal} aria-label="Close gallery">
              <X size={22} strokeWidth={2} aria-hidden />
            </ModalCloseButton>
            <ModalNavButton
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                goPrev();
              }}
              style={{ left: '-1rem' }}
              aria-label="Previous image"
            >
              <ChevronLeft size={26} strokeWidth={2} aria-hidden />
            </ModalNavButton>
            <ModalMainImage>
              <ModalImg
                src={normalizeImageUrl(urls[modalIndex])}
                alt={`${productName} — ${modalIndex + 1} of ${urls.length}`}
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = PLACEHOLDER;
                }}
              />
            </ModalMainImage>
            <ModalNavButton
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                goNext();
              }}
              style={{ right: '-1rem' }}
              aria-label="Next image"
            >
              <ChevronRight size={26} strokeWidth={2} aria-hidden />
            </ModalNavButton>
            <ModalCounter>
              {modalIndex + 1} / {urls.length}
            </ModalCounter>
            <ModalThumbnailStrip>
              {urls.map((img, index) => (
                <ModalThumbButton
                  key={index}
                  type="button"
                  $active={modalIndex === index}
                  onClick={() => setModalIndex(index)}
                  aria-label={`Image ${index + 1}`}
                >
                  <ModalThumbImg
                    src={normalizeImageUrl(img)}
                    alt=""
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = PLACEHOLDER;
                    }}
                  />
                </ModalThumbButton>
              ))}
            </ModalThumbnailStrip>
          </ModalContent>
        </ModalOverlay>
      ) : null}
    </>
  );
}
